import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from '../../common/Toast/toast';
import { useNavigate } from "react-router-dom";
import { AlertCircle, BarChart3, Download, Users } from "lucide-react";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import ExportDialog from "../../common/Export/ExportDialog";
import NewCustomAsyncDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import { getBatches, getBatchById } from "../../../../services/BatchService";
import { listAttendances } from "../../../../services/MarkattendancesService";
import { useAuth } from "../../../../context/AuthContext";
// Built to the same plan as /batch-report, so it reads that page's two
// stylesheets rather than keeping one of its own.
import "../../Trainer/TrainerDashboard.css";
import "../../common/lms-pages.css";
import FilterSheet from "../../common/FilterSheet/FilterSheet";

const DASH = "—";
const BATCH_PAGE_SIZE = 20;
const TOPIC_PREVIEW = 18;

// The two thresholds the whole page agrees on: below 50 is the case worth
// acting on, 75 is the mark a batch is expected to hold. 85 bands a row as
// strong, the same figure the trainer dashboard's attendance tab uses.
const LOW = 50;
const GOOD = 75;
const STRONG = 85;

const dateOf = (value) => {
  if (!value) return DASH;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

// Label above the figure, not below: a caption under an oversized number
// reads as a poster; above it, it reads as a report. The tone is a 2px rule
// on the top edge, never a fill behind the number.
const StatCard = ({ label, value, note, tone = "" }) => (
  <div className={`br-stat ${tone}`.trim()}>
    <span className="br-stat-label">{label}</span>
    <span className="br-stat-value">{value}</span>
    {note ? <span className="srp-stat-note">{note}</span> : null}
  </div>
);

// One fact about the batch. Rendered only when there is something to say,
// so a batch with no trainer or no end date does not leave a stub behind.
const Fact = ({ label, value }) =>
  value ? (
    <div className="srp-fact">
      <span className="srp-fact-label">{label}</span>
      <span className="srp-fact-value">{value}</span>
    </div>
  ) : null;

// A relation the API resolved is an object; one it could not resolve is
// left as the raw id string, and an empty fact reads better than an
// ObjectId.
const nameOf = (relation, key) => {
  if (!relation || typeof relation !== "object") return "";
  const value = relation[key];
  return typeof value === "string" ? value.trim() : "";
};

const StudentsReport = () => {
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!hasPermission("students-report", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view this report.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // -- The batch picker --------------------------------------------
  // A page of batches at a time. The old version asked for 500 in one
  // request, which the API clamps to 100 anyway, so the picker silently
  // could not reach most of them.
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchSearch, setBatchSearch] = useState("");
  const [batchHasMore, setBatchHasMore] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const loadBatches = useCallback(async (term, page, append) => {
    setBatchLoading(true);
    try {
      const res = await getBatches(BATCH_PAGE_SIZE, page, term);
      const list = Array.isArray(res?.data) ? res.data : [];
      const options = list.map((b) => ({
        value: b.id,
        label: b.name || "Unnamed batch",
      }));
      setBatchOptions((prev) => {
        if (!append) return options;
        const seen = new Set(prev.map((o) => o.value));
        return [...prev, ...options.filter((o) => !seen.has(o.value))];
      });
      const totalPages = Number(res?.pagination?.totalPages) || 1;
      setBatchHasMore(Boolean(res?.pagination?.hasNext) || page < totalPages);
    } catch (err) {
      console.error("Failed to load batches:", err);
    } finally {
      setBatchLoading(false);
    }
  }, []);

  // -- The report ---------------------------------------------------
  const [batchData, setBatchData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [topicsCovered, setTopicsCovered] = useState([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [lastSession, setLastSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // -- Filters on the roster, not on the request --------------------
  // The whole batch is already in hand, so these narrow it in place.
  const [search, setSearch] = useState("");
  const [band, setBand] = useState("all");

  const extractUserIds = useCallback((v) => {
    if (Array.isArray(v)) {
      return v
        .map((u) =>
          u && typeof u === "object"
            ? String(u.id || u._id || "")
            : String(u || "")
        )
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (typeof v === "string") {
      const s = v.trim();
      if (!s || s.toLowerCase() === "no students present") return [];
      return s
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }
    return [];
  }, []);

  const fetchAllAttendance = useCallback(async (batchId) => {
    const first = await listAttendances({ batchId, limit: 100, page: 1 });
    let allRecords = first?.data || [];
    const total = first?.pagination?.total || allRecords.length;
    const totalPages = Math.ceil(total / 100);

    if (totalPages > 1) {
      const pages = Array.from({ length: totalPages - 1 }, (_, i) =>
        listAttendances({ batchId, limit: 100, page: i + 2 })
      );
      const results = await Promise.all(pages);
      results.forEach((res) => {
        allRecords = allRecords.concat(res?.data || []);
      });
    }
    return allRecords;
  }, []);

  const generateReport = useCallback(
    async (batchId) => {
      if (!batchId) return;
      setLoading(true);
      setFailed(false);
      setShowAllTopics(false);
      setSearch("");
      setBand("all");

      try {
        const [batchRes, attendanceRecords] = await Promise.all([
          getBatchById(batchId),
          fetchAllAttendance(batchId),
        ]);

        const batch = batchRes?.data;
        if (!batch) {
          toast.error("Batch not found");
          setFailed(true);
          setLoading(false);
          return;
        }

        setBatchData(batch);

        // The API resolves batch.userId into the student records it could
        // find and keeps the original comma-separated list on userIdsCsv.
        // The CSV is the roster: a student whose record no longer resolves
        // is still enrolled, and reading the resolved array instead would
        // quietly shrink the count. Older responses put the CSV on userId
        // itself, so both shapes are read.
        const studentObjects = Array.isArray(batch.userId) ? batch.userId : [];
        const csv =
          typeof batch.userIdsCsv === "string"
            ? batch.userIdsCsv
            : typeof batch.userId === "string"
              ? batch.userId
              : "";
        const rosterIds = extractUserIds(csv);
        const allStudentIds = rosterIds.length
          ? rosterIds
          : extractUserIds(studentObjects);

        const studentMap = new Map();
        studentObjects.forEach((s) => {
          if (s && s.id) studentMap.set(String(s.id), s);
          if (s && s._id) studentMap.set(String(s._id), s);
        });

        const totalSessions = attendanceRecords.length;

        // One pass over the sessions rather than one per student: the
        // nested loop this replaces was students x sessions, which for a
        // year-long batch of sixty ran fifteen thousand times.
        const presence = new Map();
        attendanceRecords.forEach((record) => {
          const when = record?.date ? new Date(record.date) : null;
          const marked = when && !Number.isNaN(when.getTime()) ? when : null;
          extractUserIds(record.userIds).forEach((sid) => {
            const seen = presence.get(sid) || { count: 0, last: null };
            seen.count += 1;
            if (marked && (!seen.last || marked > seen.last)) seen.last = marked;
            presence.set(sid, seen);
          });
        });

        const report = allStudentIds.map((sid) => {
          const student = studentMap.get(sid);
          const seen = presence.get(sid) || { count: 0, last: null };

          return {
            studentId: sid,
            name: student?.name || "Unknown student",
            email: student?.email || "",
            phone: student?.phone || "",
            present: seen.count,
            absent: totalSessions - seen.count,
            total: totalSessions,
            // Null, not zero, when nothing has been marked yet. A batch
            // with no sessions is not a batch where everybody failed to
            // turn up, and 0% would band every row red.
            percentage:
              totalSessions > 0
                ? Math.round((seen.count / totalSessions) * 100)
                : null,
            lastPresent: seen.last,
          };
        });

        report.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "en", {
            sensitivity: "base",
          })
        );

        setReportData(report);
        setSessionCount(totalSessions);

        const sessionDates = attendanceRecords
          .map((r) => (r?.date ? new Date(r.date) : null))
          .filter((d) => d && !Number.isNaN(d.getTime()));
        setLastSession(
          sessionDates.length
            ? new Date(Math.max(...sessionDates.map((d) => d.getTime())))
            : null
        );

        const topicsSet = new Set();
        attendanceRecords.forEach((record) => {
          const syl = record.syllabusId;
          if (Array.isArray(syl)) {
            syl.forEach((s) => {
              if (s && typeof s === "object" && s.title) topicsSet.add(s.title);
              else if (typeof s === "string" && s.trim())
                topicsSet.add(s.trim());
            });
          } else if (typeof syl === "string" && syl.trim()) {
            syl.split(",").forEach((t) => {
              const cleaned = t.trim().replace(/(?:-2)+$/, "");
              if (
                cleaned &&
                cleaned !== "No topics" &&
                !/^[a-f\d]{24}$/i.test(cleaned)
              ) {
                topicsSet.add(cleaned);
              }
            });
          }
        });
        setTopicsCovered([...topicsSet]);
      } catch (error) {
        console.error("Error generating report:", error);
        toast.error("Failed to generate report");
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [extractUserIds, fetchAllAttendance]
  );

  useEffect(() => {
    if (selectedBatch?.value) {
      generateReport(selectedBatch.value);
      return;
    }
    setBatchData(null);
    setReportData([]);
    setTopicsCovered([]);
    setSessionCount(0);
    setLastSession(null);
    setFailed(false);
  }, [selectedBatch, generateReport]);

  // -- What the table actually draws --------------------------------
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reportData.filter((r) => {
      if (term) {
        const haystack = `${r.name} ${r.email} ${r.phone}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (band === "all") return true;
      // A student with no sessions to be measured against belongs to no
      // band, so every band excludes them rather than one claiming them.
      if (r.percentage === null) return false;
      if (band === "low") return r.percentage < LOW;
      if (band === "mid") return r.percentage >= LOW && r.percentage < GOOD;
      return r.percentage >= GOOD;
    });
  }, [reportData, search, band]);

  const stats = useMemo(() => {
    const measured = reportData.filter((r) => r.percentage !== null);
    return {
      students: reportData.length,
      sessions: sessionCount,
      avg: measured.length
        ? Math.round(
            measured.reduce((sum, r) => sum + r.percentage, 0) / measured.length
          )
        : null,
      belowMark: measured.filter((r) => r.percentage < GOOD).length,
    };
  }, [reportData, sessionCount]);

  /*
   * The report's columns, for the shared export popup. The hand-rolled
   * CSV this replaced wrapped its own fields in quotes and left the
   * numbers bare, which meant a student whose name held a quote broke
   * the row; the writer escapes now and this only says what a cell is.
   */
  const exportColumns = [
    { key: "sr", label: "Sr. No", value: (_r, i) => i + 1 },
    { key: "name", label: "Student Name", value: (r) => r.name },
    { key: "email", label: "Email", value: (r) => r.email || DASH },
    { key: "phone", label: "Phone", value: (r) => r.phone || DASH },
    { key: "present", label: "Present", value: (r) => r.present },
    { key: "absent", label: "Absent", value: (r) => r.absent },
    { key: "total", label: "Total Sessions", value: (r) => r.total },
    {
      key: "percentage",
      label: "Attendance %",
      value: (r) => (r.percentage === null ? DASH : `${r.percentage}%`),
    },
    {
      key: "lastPresent",
      label: "Last Present",
      value: (r) => (r.lastPresent ? dateOf(r.lastPresent) : DASH),
    },
    {
      key: "status",
      label: "Status",
      value: (r) =>
        r.percentage === null
          ? "Not marked"
          : r.percentage >= GOOD
            ? "Good"
            : r.percentage >= LOW
              ? "Average"
              : "Low",
    },
  ];

  /*
   * The lines above and below the table. They are not columns -- what
   * the report is of, when it was run, and what has been taught -- so
   * they ride as whole lines rather than as a row of cells.
   */
  const exportPreamble = [
    `Students Report - ${batchData?.name || "Batch"}`,
    `Generated: ${new Date().toLocaleDateString()}`,
    `Students: ${stats.students} | Sessions: ${stats.sessions} | Average attendance: ${
      stats.avg === null ? DASH : `${stats.avg}%`
    }`,
  ];

  const exportAppendix = topicsCovered.length
    ? [
        "Topics covered till date:",
        ...topicsCovered.map((t, i) => `${i + 1}. ${t}`),
      ]
    : [];

  const headers = useMemo(
    () => [
      {
        title: "Student",
        prop: "name",
        isSortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="lp-name">{row.name}</span>
            {row.email ? <span className="lp-sub">{row.email}</span> : null}
          </div>
        ),
      },
      {
        title: "Phone",
        prop: "phone",
        cell: (row) =>
          row.phone ? (
            <span className="lp-contact lp-contact--tel">{row.phone}</span>
          ) : (
            <span className="lp-muted">{DASH}</span>
          ),
      },
      {
        title: "Sessions",
        prop: "total",
        isSortable: true,
        thProps: { className: "td-num" },
        cellProps: { className: "td-num" },
        cell: (row) => <span className="lp-num">{row.total}</span>,
      },
      {
        title: "Present",
        prop: "present",
        isSortable: true,
        thProps: { className: "td-num" },
        cellProps: { className: "td-num" },
        cell: (row) => <span className="lp-num">{row.present}</span>,
      },
      {
        // Absences are the figure worth noticing, so a clean record stays
        // quiet rather than printing a bold zero.
        title: "Absent",
        prop: "absent",
        isSortable: true,
        thProps: { className: "td-num" },
        cellProps: { className: "td-num" },
        cell: (row) =>
          row.absent > 0 ? (
            <span className="lp-num">{row.absent}</span>
          ) : (
            <span className="lp-muted">0</span>
          ),
      },
      {
        // The one place colour lands on a figure rather than on a pill.
        // No meter in the row: sixty small bars down a column read as
        // noise, and the row band already says which rows to look at.
        title: "Attendance",
        prop: "percentage",
        isSortable: true,
        thProps: { className: "td-num" },
        cellProps: { className: "td-num" },
        cell: (row) => {
          if (row.percentage === null)
            return <span className="lp-muted">{DASH}</span>;
          const tone =
            row.percentage >= GOOD
              ? "is-strong"
              : row.percentage >= LOW
                ? "is-fair"
                : "is-weak";
          return <span className={`srp-att ${tone}`}>{row.percentage}%</span>;
        },
      },
      {
        title: "Last present",
        prop: "lastPresent",
        isSortable: true,
        cell: (row) =>
          row.lastPresent ? (
            <span className="lp-when">{dateOf(row.lastPresent)}</span>
          ) : (
            <span className="lp-muted">{DASH}</span>
          ),
      },
    ],
    []
  );

  const visibleTopics = showAllTopics
    ? topicsCovered
    : topicsCovered.slice(0, TOPIC_PREVIEW);

  const courseName = nameOf(batchData?.courseId, "name");
  const trainerName = nameOf(batchData?.trainerId, "trainerName");
  const classroomNo = nameOf(batchData?.classroom, "classroomNo");
  const syllabusTotal = Number(batchData?.syllabusTopicCount) || 0;
  const hasFilters = search.trim() !== "" || band !== "all";

  if (!hasPermission("students-report", "view", "lms")) {
    return null;
  }

  return (
    <div className="pg lp srp">

      <header className="pg-head">
        <div className="pg-head-main">
          <h4 className="pg-title">Students report</h4>
          <p className="pg-sub">
            One batch at a time: who is enrolled, how often each of them turned
            up, and what has been taught so far.
          </p>
        </div>
        <div className="pg-head-actions">
          {reportData.length > 0 && (
            <span className="lp-count">
              {rows.length === reportData.length
                ? `${reportData.length} students`
                : `${rows.length} of ${reportData.length} students`}
            </span>
          )}
          <button
            type="button"
            className="pg-btn"
            onClick={() => setExportOpen(true)}
            disabled={rows.length === 0}
            aria-haspopup="dialog"
            aria-expanded={exportOpen}
          >
            {/* Sized here rather than in the stylesheet: lucide defaults to
                24px, and only .pg-filters .pg-btn svg is written down. */}
            <Download size={14} aria-hidden="true" />
            Export
          </button>
        </div>
      </header>

      <FilterSheet
        label="Choose a batch"
        title="Choose a batch"
        triggerLabel="Choose a batch"
      >
        <div className="pg-filters-row">
          <div className="pg-filters-fields srp-fields">
            <div className="pg-field">
              <label className="pg-label">Batch</label>
              <NewCustomAsyncDropdown
                options={batchOptions}
                value={selectedBatch ? [selectedBatch] : []}
                onChange={(selected) =>
                  setSelectedBatch(
                    Array.isArray(selected) && selected.length > 0
                      ? selected[0]
                      : null
                  )
                }
                placeholder="Choose a batch"
                isMulti={false}
                isSearchable={true}
                isLoading={batchLoading}
                searchInControl={true}
                onMenuOpen={() => {
                  if (batchOptions.length === 0 && !batchLoading) {
                    setBatchPage(1);
                    setBatchSearch("");
                    loadBatches("", 1, false);
                  }
                }}
                onInputChange={(term) => {
                  setBatchSearch(term);
                  setBatchPage(1);
                  loadBatches(term, 1, false);
                }}
                onMenuScrollToBottom={() => {
                  if (batchHasMore && !batchLoading) {
                    const next = batchPage + 1;
                    setBatchPage(next);
                    loadBatches(batchSearch, next, true);
                  }
                }}
                onRefresh={() => {
                  setBatchSearch("");
                  setBatchPage(1);
                  loadBatches("", 1, false);
                }}
              />
            </div>

            <div className="pg-field">
              <label className="pg-label" htmlFor="srp-search">
                Find a student
              </label>
              <input
                id="srp-search"
                type="search"
                className="pg-input"
                placeholder="Name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={reportData.length === 0}
              />
            </div>

            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="srp-band">
                Attendance
              </label>
              <select
                id="srp-band"
                className="pg-select"
                value={band}
                onChange={(e) => setBand(e.target.value)}
                disabled={reportData.length === 0}
              >
                <option value="all">All</option>
                <option value="low">{`Below ${LOW}%`}</option>
                <option value="mid">{`${LOW} to ${GOOD - 1}%`}</option>
                <option value="good">{`${GOOD}% and above`}</option>
              </select>
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn"
              onClick={() => {
                setSearch("");
                setBand("all");
              }}
              disabled={!hasFilters}
            >
              Clear
            </button>
          </div>
        </div>
      </FilterSheet>

      {!selectedBatch && !loading && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <BarChart3 size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">Pick a batch to build the report</h3>
          <p className="pg-state-text">
            Choose a batch above to see its roster, each student&apos;s
            attendance and the topics covered so far.
          </p>
        </div>
      )}

      {/* The toast that reported the failure fades; without this the page
          is left as a filter bar above nothing, with no way to tell a
          broken request from a batch that simply has no one on it. */}
      {failed && !loading && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <AlertCircle size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">Could not build this report</h3>
          <p className="pg-state-text">
            The batch or its attendance did not load. Pick the batch again to
            retry.
          </p>
        </div>
      )}

      {batchData && !failed && (
        <>
          {/* The batch, said once, so no column has to repeat it. */}
          <section className="srp-batch" aria-label="Batch details">
            <h5 className="srp-batch-name">
              {batchData.name || "Unnamed batch"}
            </h5>
            <div className="srp-facts">
              <Fact label="Course" value={courseName} />
              <Fact label="Trainer" value={trainerName} />
              <Fact label="Classroom" value={classroomNo} />
              <Fact label="Mode" value={batchData.mode} />
              <Fact
                label="Runs"
                value={
                  batchData.startTime
                    ? `${dateOf(batchData.startTime)} to ${dateOf(
                        batchData.endTime
                      )}`
                    : ""
                }
              />
              <Fact
                label="Last session"
                value={lastSession ? dateOf(lastSession) : ""}
              />
            </div>
          </section>

          <div className="br-stats">
            <StatCard label="Students" value={stats.students} />
            <StatCard label="Sessions held" value={stats.sessions} />
            <StatCard
              label="Average attendance"
              value={stats.avg === null ? DASH : `${stats.avg}%`}
              tone={
                stats.avg === null
                  ? ""
                  : stats.avg >= GOOD
                    ? "br-stat--good"
                    : stats.avg >= LOW
                      ? "br-stat--warn"
                      : "br-stat--bad"
              }
            />
            <StatCard
              label={`Below ${GOOD}%`}
              value={stats.belowMark}
              tone={stats.belowMark > 0 ? "br-stat--warn" : ""}
            />
            <StatCard
              label="Topics covered"
              value={topicsCovered.length}
              note={
                syllabusTotal > 0 ? `of ${syllabusTotal} in the syllabus` : null
              }
            />
          </div>
        </>
      )}

      {batchData && !failed && reportData.length > 0 && (
        <>
          {/* td-list, but not td-list--lms: that variant makes every row
              a pointer because those pages open a record on click, and
              nothing opens here. */}
          <div className="td-list td-list--srp">
            <NewCustomDataTable
              data={rows}
              headers={headers}
              totalRecords={rows.length}
              currentPage={1}
              recordsPerPage={rows.length || 1}
              onPageChange={() => {}}
              // serverSide in the sense that this page hands the table the
              // rows it should draw: one batch is already in hand, so it is
              // filtered here and drawn in full, and the table's own sort
              // then works across the whole roster rather than a page of it.
              serverSide={true}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              loading={loading}
              rowProps={(row) => ({
                className:
                  row.percentage === null
                    ? undefined
                    : row.percentage < LOW
                      ? "is-low"
                      : row.percentage >= STRONG
                        ? "is-high"
                        : undefined,
              })}
            />
          </div>

          {topicsCovered.length > 0 && (
            <section className="srp-topics" aria-label="Topics covered">
              <header className="srp-topics-head">
                <h5 className="pgm-section-title">Topics covered till date</h5>
                <span className="lp-count">
                  {syllabusTotal > 0
                    ? `${topicsCovered.length} / ${syllabusTotal}`
                    : `${topicsCovered.length} topics`}
                </span>
              </header>

              <ol className="pg-topiclist srp-topiclist">
                {visibleTopics.map((topic, idx) => (
                  <li className="pg-topiclist-row" key={`${topic}-${idx}`}>
                    <span className="pg-topiclist-num">{idx + 1}</span>
                    <span className="pg-topiclist-text">{topic}</span>
                  </li>
                ))}
              </ol>

              {topicsCovered.length > TOPIC_PREVIEW && (
                <div className="srp-topics-foot">
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setShowAllTopics((on) => !on)}
                    aria-expanded={showAllTopics}
                  >
                    {showAllTopics
                      ? "Show fewer"
                      : `Show all ${topicsCovered.length}`}
                  </button>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {batchData && !failed && reportData.length === 0 && !loading && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Users size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">Nobody is enrolled on this batch</h3>
          <p className="pg-state-text">
            Add students to the batch and their attendance is reported here.
          </p>
        </div>
      )}

      {batchData && !failed && reportData.length > 0 && rows.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Users size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No student matches these filters</h3>
          <p className="pg-state-text">
            {`${reportData.length} students are enrolled on this batch. Clear the search or the attendance band to see them.`}
          </p>
        </div>
      )}

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={`Students report — ${batchData?.name || "Batch"}`}
        columns={exportColumns}
        rows={rows}
        filenameBase={`students-report-${batchData?.name || "batch"}`}
        sheetName="Students report"
        preamble={exportPreamble}
        appendix={exportAppendix}
        scopeNote={
          rows.length === reportData.length
            ? undefined
            : `${rows.length} of ${reportData.length} enrolled students match the filters on the table.`
        }
      />
    </div>
  );
};

export default StudentsReport;
